// Excel-КП от ООО «МосЭл» — та же раскладка, что у PDF по бланку РОЗ: логотип
// и реквизиты, синяя линия, «Исх. № …» и заказчик, заголовок, таблица 7 колонок.
// Содержание — как у Excel-КП СПЕЦМАШЭЛЕКТРО (решение 14.10.2026): «ВСЕГО с НДС
// 22%», «В том числе НДС 22%», условия 1–7, исполнитель; строка подписанта
// текстом (без подписи и печати); внизу каждого листа печати — «КП № … · Лист N
// из M». Тексты — общие с PDF (kpMosel.ts, kpTerms.ts). Шрифт — Times New Roman,
// как в бланке. Строку «Кому» в Excel не печатаем, как у СПЕЦМАШЭЛЕКТРО.
import ExcelJS from 'exceljs'
import type { VatMode } from '@crm/shared'
import type { KpPdfItem } from './generateKpPdf'
import type { KpEntity } from './kpEntities'
import { loadKpLogoBase64 } from './kpLogo'
import {
  buildMoselRows,
  kpDateStr,
  MOSEL_COL_ALIGN,
  MOSEL_COL_MM,
  MOSEL_REQ_COLOR,
  MOSEL_RULE_COLOR,
  MOSEL_SIGN_BLANK,
  MOSEL_TITLE,
  moselFileName,
  moselHeaders,
  moselOutgoingLine,
  moselSigner,
} from './kpMosel'
import { excelPageMarkFooter, KP_EXCEL_TERMS, KP_TERMS_TITLE, kpResponsibleLines } from './kpTerms'
import { kpTermsFor, kpVatView } from './kpVat'

export interface KpMoselExcelParams {
  entity: KpEntity
  requestNumber?: string
  clientName?: string | null
  /** Кто выставляет КП — «Исполнитель / Ответственный», как у СПЕЦМАШЭЛЕКТРО. */
  responsibleName?: string | null
  responsiblePhone?: string | null
  responsibleEmail?: string | null
  /** Режим НДС КП: «С НДС» (по умолчанию) или «НДС сверху». */
  vatMode?: VatMode
  items: KpPdfItem[]
}

const FONT_NAME = 'Times New Roman'
const MM_TO_PT = 72 / 25.4
/** Ширина колонки Excel (в «нулях» шрифта по умолчанию) из миллиметров бланка. */
const colWidth = (mm: number) => Math.round((((mm * 96) / 25.4 - 5) / 7) * 10) / 10
/** Сколько символов 12 pt примерно помещается в колонку (для высоты строки). */
const charsPerLine = (mm: number) => Math.max(4, Math.floor((mm - 3.8) / 2.4))

const font = (size: number, bold = false, color?: string): Partial<ExcelJS.Font> => ({
  name: FONT_NAME,
  size,
  bold,
  ...(color ? { color: { argb: `FF${color}` } } : {}),
})
const thin: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FF000000' } }
const BORDER: Partial<ExcelJS.Borders> = { top: thin, left: thin, bottom: thin, right: thin }

/** Высота строки реквизитов 8 pt: блок D:G ~86 мм; заглавная ~1,6 мм, прочие знаки ~1,25 мм. */
const reqRowHeight = (line: string) => {
  const mm = [...line].reduce((w, ch) => w + (ch !== ch.toLowerCase() ? 1.6 : 1.25), 0)
  return Math.max(1, Math.ceil(mm / 86)) * 13.6
}

const estimateLines = (text: string, mm: number) =>
  text.split('\n').reduce((n, p) => n + Math.max(1, Math.ceil(p.length / charsPerLine(mm))), 0)

export async function generateKpExcelMosel({
  entity,
  requestNumber,
  clientName,
  responsibleName,
  responsiblePhone,
  responsibleEmail,
  vatMode,
  items,
}: KpMoselExcelParams): Promise<void> {
  const view = kpVatView(items, vatMode)
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('КП', {
    views: [{ showGridLines: false }],
    pageSetup: {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.5, right: 0.5, top: 0.5, bottom: 0.75, header: 0, footer: 0.3 },
    },
  })
  ws.columns = MOSEL_COL_MM.map((mm) => ({ width: colWidth(mm) }))
  const last = MOSEL_COL_MM.length // G

  const merge = (row: number, from: number, to: number) => {
    if (to > from) ws.mergeCells(row, from, row, to)
    return ws.getRow(row).getCell(from)
  }

  // ── Шапка: логотип слева (A1), реквизиты справа (D:G), линия под шапкой ──
  let row = 1
  ws.getRow(row).height = 14
  row++
  const reqLines = [...entity.requisites, ...entity.contacts]
  for (const line of reqLines) {
    const cell = merge(row, 4, last)
    cell.value = line
    cell.font = font(8, false, MOSEL_REQ_COLOR)
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 }
    ws.getRow(row).height = reqRowHeight(line)
    row++
  }
  // Низ шапки — на высоте линии бланка (41 мм от верха логотипа).
  const used = 14 + reqLines.reduce((h, l) => h + reqRowHeight(l), 0)
  ws.getRow(row).height = Math.max(8, 41 * MM_TO_PT - used)
  for (let c = 1; c <= last; c++) {
    ws.getRow(row).getCell(c).border = {
      bottom: { style: 'medium', color: { argb: `FF${MOSEL_RULE_COLOR}` } },
    }
  }
  row++
  const logo = await loadKpLogoBase64(entity.logo.url)
  if (logo) {
    const imageId = wb.addImage({ base64: logo, extension: 'png' })
    ws.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: entity.logo.width, height: entity.logo.height },
    })
  }

  // ── «Исх. № … от …г.» слева, заказчик справа жирным ──
  const dateStr = kpDateStr()
  ws.headerFooter.oddFooter = excelPageMarkFooter({
    requestNumber,
    dateStr,
    entityName: entity.shortName,
    clientName,
  })
  const out = merge(row, 1, 3)
  out.value = moselOutgoingLine(requestNumber, dateStr)
  out.font = font(12)
  out.alignment = { vertical: 'top', horizontal: 'left' }
  const client = clientName?.trim() ?? ''
  const clientCell = merge(row, 4, last)
  clientCell.value = client
  clientCell.font = font(12, true)
  clientCell.alignment = { vertical: 'top', horizontal: 'right', wrapText: true }
  ws.getRow(row).height = Math.max(20, estimateLines(client, 90) * 15.9 + 4)
  row++
  ws.getRow(row).height = 42
  row++

  // ── Заголовок ──
  const title = merge(row, 1, last)
  title.value = MOSEL_TITLE
  title.font = font(12, true)
  title.alignment = { vertical: 'middle', horizontal: 'center' }
  ws.getRow(row).height = 20
  row++
  ws.getRow(row).height = 11
  row++

  // ── Таблица ──
  moselHeaders(view.onTop).forEach((h, i) => {
    const cell = ws.getRow(row).getCell(i + 1)
    cell.value = h
    cell.font = font(12, true)
    cell.border = BORDER
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })
  ws.getRow(row).height = 58
  // Шапка таблицы повторяется на каждом листе печати.
  ws.pageSetup.printTitlesRow = `${row}:${row}`
  row++

  const rows = buildMoselRows(items, view)
  for (const r of rows) {
    const vals: (string | number)[] = [r.no, r.name, r.offer, r.qty, r.price, r.sum, r.delivery]
    const lines = Math.max(
      1,
      ...vals.map((v, i) => (typeof v === 'string' ? estimateLines(v, MOSEL_COL_MM[i]) : 1)),
    )
    vals.forEach((v, i) => {
      const cell = ws.getRow(row).getCell(i + 1)
      cell.value = v
      cell.font = font(12)
      cell.border = BORDER
      cell.alignment = { vertical: 'middle', horizontal: MOSEL_COL_ALIGN[i], wrapText: true }
      if (i === 4 || i === 5) cell.numFmt = '#,##0.00'
    })
    // Минимальная высота строки в бланке — 9,1 мм.
    ws.getRow(row).height = Math.max(26, lines * 15.9 + 10)
    row++
  }

  // ── Итоги: подпись на колонки A–E, сумма в «Стоимости с НДС» ──
  for (const { label, value } of view.rows) {
    const cell = merge(row, 1, 5)
    cell.value = label
    cell.font = font(12, true)
    cell.border = BORDER
    cell.alignment = { vertical: 'middle', horizontal: 'right' }
    const sum = ws.getRow(row).getCell(6)
    sum.value = value
    sum.numFmt = '#,##0.00'
    sum.font = font(12, true)
    sum.border = BORDER
    sum.alignment = { vertical: 'middle', horizontal: 'center' }
    ws.getRow(row).height = 22
    row++
  }

  // ── Условия поставки 1–7 — тексты Excel-КП СПЕЦМАШЭЛЕКТРО ──
  ws.getRow(row).height = 14
  row++
  const termsTitle = merge(row, 1, last)
  termsTitle.value = `${KP_TERMS_TITLE}:`
  termsTitle.font = font(12, true)
  termsTitle.alignment = { vertical: 'top', horizontal: 'left', indent: 1 }
  row++
  for (const term of kpTermsFor(KP_EXCEL_TERMS, view.onTop)) {
    const cell = merge(row, 1, last)
    cell.value = term
    cell.font = font(10)
    cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 }
    // 10 pt — примерно на 20 % больше знаков в строке, чем у 12 pt.
    ws.getRow(row).height = estimateLines(term, 205) * 13.2 + 3
    row++
  }

  // ── Исполнитель / Ответственный / Телефон / Почта ──
  ws.getRow(row).height = 12
  row++
  for (const line of kpResponsibleLines(responsibleName, responsiblePhone, responsibleEmail)) {
    const cell = merge(row, 1, last)
    cell.value = line
    cell.font = font(11)
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    ws.getRow(row).height = 16
    row++
  }

  // ── Место под подпись и печать (ставятся от руки) и строка подписанта ──
  ws.getRow(row).height = 60
  row++
  const signer = moselSigner(entity)
  const who = merge(row, 1, 3)
  who.value = signer.title
  who.font = font(12, true)
  who.alignment = { vertical: 'bottom', horizontal: 'left', indent: 3 }
  const blank = merge(row, 4, 5)
  blank.value = MOSEL_SIGN_BLANK
  blank.font = font(12)
  blank.alignment = { vertical: 'bottom', horizontal: 'left' }
  const name = merge(row, 6, last)
  name.value = signer.name
  name.font = font(12, true)
  name.alignment = { vertical: 'bottom', horizontal: 'right' }
  ws.getRow(row).height = 20

  // ── Скачивание ──
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = moselFileName(clientName, requestNumber, dateStr, 'xlsx')
  a.click()
  URL.revokeObjectURL(url)
}
